import { CommonModule } from '@angular/common';
import { Component, ViewChild, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, Observable, of } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { PageHeaderComponent } from '@shared/components/page-header/page-header.component';
import { FinanceManagementService } from '@modules/external-services/finance-management/service/finance-management.service';
import {
  BankGuaranteeByUuidSelectors,
  CashGuaranteeByUuidSelectors,
} from '@modules/external-services/finance-management/store/finance-management.selectors';
import { ApprovalTimelineComponent } from '@shared/components/approval-timeline/approval-timeline.component';
import { ApprovalFormComponent } from '@modules/workflow/components/approval-form/approval-form.component';
import { loadWorkflowInstance, loadWorkflowStatus } from '@modules/workflow/store/workflow.actions';
import { selectWorkflowStatus } from '@modules/workflow/store/workflow.selectors';
import { WorkflowStatus } from '@modules/workflow/responses/workflow-status.response';
import { OmcRegistrationService } from '@modules/registration/omc-registration/service/omc-registration.service';
import { AttachmentSelectors } from '@modules/registration/omc-registration/store/omc-registration.selectors';
import { CustomAppModal, CustomAppModalData } from '@components/custom-app-modal/custom-app-modal';
import { ExternalAttachmentPreview } from '@components/custom-app-modal/external-attachment-preview/external-attachment-preview';
import { LoadingSpinner } from '@components/loading-spinner/loading-spinner';
import { ExternalServiceStatusStyleService } from '@modules/external-services/shared/services/external-service-status-style.service';

interface AttachmentTypeDetail {
  name?: string | null;
  fileType?: string | null;
}

interface AttachableDetail {
  uuid?: string | null;
  registrationType?: string | null;
  bucketName?: string | null;
  attachableDiscriminator?: string | null;
}

interface AttachmentDetail {
  uuid?: string | null;
  otherAttachmentName?: string | null;
  minioObjectName?: string | null;
  createdAt?: string | null;
  attachmentType?: AttachmentTypeDetail | null;
  attachable?: AttachableDetail | null;
}

interface CashGuaranteeDetail {
  omc?: {
    companyName?: string | null;
    name: string | null;
  } | null;
  active?: boolean | null;
  amount?: number | null;
  attachableDiscriminator?: string | null;
  attachments?: AttachmentDetail[] | null;
  bankDetail?: {
    bankName?: string | null;
  } | null;
  bucketName?: string | null;
  createdAt?: string | null;
  createdBy?: string | null;
  currency?: {
    code?: string | null;
  } | null;
  currencyName?: string | null;
  currencyUuid?: string | null;
  depositDate?: string | null;
  depositReference?: string | null;
  evidenceDocumentRefsJson?: string | null;
  omcUuid?: string | null;
  otherAttachmentName?: string | null;
  pbpaAccount?: string | null;
  registrationType?: string | null;
  status?: string | null;
}

interface ManualGuaranteeDetail {
  bankConfiguration?: {
    name?: string | null;
  } | null;
  attachments?: AttachmentDetail[] | null;
  active?: boolean | null;
  amount?: number | null;
  attachableDiscriminator?: string | null;
  bankConfigUuid?: string | null;
  createdAt?: string | null;
  createdBy?: string | null;
  currency?: {
    code?: string | null;
  } | null;
  currencyName?: string | null;
  currencyUuid?: string | null;
  expiryDate?: string | null;
  issueDate?: string | null;
  referenceNumber?: string | null;
  registrationType?: string | null;
  status?: string | null;
  tin?: string | null;
  omc?: {
    name: string | null;
    uuid?: string | null;
  };
}

type GuaranteeViewType = 'cash' | 'manual';

@Component({
  selector: 'guarantee-view',
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    MatIconModule,
    ApprovalTimelineComponent,
    ApprovalFormComponent,
    CustomAppModal,
    LoadingSpinner,
  ],
  templateUrl: './guarantee-view.html',
  styleUrl: './guarantee-view.scss',
})
export class GuaranteeView implements OnInit {
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private financeManagementService = inject(FinanceManagementService);
  private omcRegistrationService = inject(OmcRegistrationService);
  readonly statusStyle = inject(ExternalServiceStatusStyleService);
  @ViewChild('modal') modal!: CustomAppModal;

  viewType: GuaranteeViewType = 'manual';
  isLoading$: Observable<boolean> = of(false);
  cashGuarantee: CashGuaranteeDetail | null = null;
  manualGuarantee: ManualGuaranteeDetail | null = null;
  workflow: WorkflowStatus | null = null;
  isReadOnlyView = false;
  guaranteeUuid: string | null = null;
  selectedAttachmentUuid = '';
  downloadingAttachments = new Set<string>();

  ngOnInit(): void {
    const uuid = this.route.snapshot.paramMap.get('uuid');
    if (!uuid) {
      return;
    }
    this.guaranteeUuid = uuid;

    this.viewType = (this.route.snapshot.data['type'] as GuaranteeViewType) ?? 'manual';
    this.isReadOnlyView = !!this.route.snapshot.data['readOnly'];

    if (this.viewType === 'cash') {
      this.isLoading$ = this.store.select(CashGuaranteeByUuidSelectors.loading);
      this.store
        .select(CashGuaranteeByUuidSelectors.response)
        .pipe(filter((response) => !!response?.data))
        .subscribe((response) => {
          this.cashGuarantee = response?.data as CashGuaranteeDetail;
        });
      this.financeManagementService.getCashGuaranteeByUuid(uuid);
    } else {
      this.isLoading$ = this.store.select(BankGuaranteeByUuidSelectors.loading);
      this.store
        .select(BankGuaranteeByUuidSelectors.response)
        .pipe(filter((response) => !!response?.data))
        .subscribe((response) => {
          this.manualGuarantee = response?.data as ManualGuaranteeDetail;
        });
      this.financeManagementService.getBankGuaranteeByUuid(uuid);
    }

    if (!this.isReadOnlyView) {
      this.loadWorkflow(uuid);

      this.store.select(selectWorkflowStatus).subscribe((status) => {
        this.workflow = status;
      });
    }

    this.store
      .select(AttachmentSelectors.response)
      .pipe(
        filter(
          (response) =>
            !!response?.data?.data && this.downloadingAttachments.has(this.selectedAttachmentUuid),
        ),
      )
      .subscribe((response) => {
        const base64 = response?.data?.data;
        if (!base64) {
          this.downloadingAttachments.delete(this.selectedAttachmentUuid);
          return;
        }

        this.openAttachmentModal(base64);
        this.downloadingAttachments.delete(this.selectedAttachmentUuid);
      });

    this.store
      .select(AttachmentSelectors.error)
      .pipe(
        filter((error) => !!error && this.downloadingAttachments.has(this.selectedAttachmentUuid)),
      )
      .subscribe(() => {
        this.downloadingAttachments.delete(this.selectedAttachmentUuid);
      });
  }

  private loadWorkflow(uuid: string): void {
    this.store.dispatch(loadWorkflowInstance({ businessKey: uuid }));
    this.store.dispatch(
      loadWorkflowStatus({
        businessKey: uuid,
        businessType: this.businessType,
      }),
    );
  }

  formatAmount(currency?: string | null, amount?: number | null): string {
    if (!currency || amount === null || amount === undefined) {
      return '-';
    }
    return `${currency} ${Number(amount).toLocaleString()}`;
  }

  formatBoolean(value?: boolean | null): string {
    if (value === null || value === undefined) {
      return '-';
    }
    return value ? 'Yes' : 'No';
  }

  get pageTitle(): string {
    return this.viewType === 'cash' ? 'Cash Guarantee' : 'Manual Bank Guarantee';
  }

  get businessType(): string | null {
    return this.viewType === 'cash' ? 'CASH_GUARANTEE_SUBMISSION ' : 'BANK_GUARANTEE_SUBMISSION ';
  }

  get currentReference(): string | null | undefined {
    return this.viewType === 'cash'
      ? this.cashGuarantee?.depositReference
      : this.manualGuarantee?.referenceNumber;
  }

  get currentCompanyName(): string | null | undefined {
    return this.viewType === 'cash'
      ? (this.cashGuarantee?.omc?.name || (this.cashGuarantee as any)?.verifiedCustomerName)
      : (this.manualGuarantee?.omc?.name || (this.manualGuarantee as any)?.verifiedCustomerName);
  }

  get currentAmount(): string {
    if (this.viewType === 'cash') {
      return this.formatAmount(
        this.cashGuarantee?.currency?.code ?? this.cashGuarantee?.currencyName,
        this.cashGuarantee?.amount,
      );
    }
    return this.formatAmount(
      this.manualGuarantee?.currency?.code ?? this.manualGuarantee?.currencyName,
      this.manualGuarantee?.amount,
    );
  }

  get currentStatus(): string | null | undefined {
    return this.viewType === 'cash' ? this.cashGuarantee?.status : this.manualGuarantee?.status;
  }

  get currentBankName(): string | null | undefined {
    return this.viewType === 'cash'
      ? this.cashGuarantee?.bankDetail?.bankName
      : this.manualGuarantee?.bankConfiguration?.name;
  }

  get currentDate(): string | null | undefined {
    return this.viewType === 'cash'
      ? this.cashGuarantee?.depositDate
      : this.manualGuarantee?.issueDate;
  }

  get expireDate(): string | null | undefined {
    return this.manualGuarantee?.expiryDate;
  }

  get currentRegistrationType(): string | null | undefined {
    return this.viewType === 'cash'
      ? this.cashGuarantee?.registrationType
      : this.manualGuarantee?.registrationType;
  }

  get currentCurrency(): string | null | undefined {
    return this.viewType === 'cash'
      ? (this.cashGuarantee?.currency?.code ?? this.cashGuarantee?.currencyName)
      : (this.manualGuarantee?.currency?.code ?? this.manualGuarantee?.currencyName);
  }

  get currentVisibility(): string | null | undefined {
    return this.viewType === 'cash'
      ? this.cashGuarantee?.attachableDiscriminator
      : this.manualGuarantee?.attachableDiscriminator;
  }

  get attachments(): AttachmentDetail[] {
    return this.viewType === 'cash'
      ? (this.cashGuarantee?.attachments ?? [])
      : (this.manualGuarantee?.attachments ?? []);
  }

  getAttachmentName(attachment: AttachmentDetail): string {
    return (
      attachment.attachmentType?.name ||
      attachment.otherAttachmentName ||
      attachment.minioObjectName ||
      'N/A'
    );
  }

  getAttachmentIcon(fileType?: string | null): string {
    const normalized = (fileType || '').toLowerCase();
    if (normalized.includes('pdf')) return 'picture_as_pdf';
    if (normalized.includes('doc')) return 'description';
    if (normalized.includes('jpg') || normalized.includes('jpeg') || normalized.includes('png')) {
      return 'image';
    }
    return 'attach_file';
  }

  viewAttachment(uuid?: string | null): void {
    if (!uuid) {
      return;
    }

    this.selectedAttachmentUuid = uuid;
    this.downloadingAttachments.add(uuid);
    this.omcRegistrationService.viewOmcAttachments(uuid);
  }

  private openAttachmentModal(base64: string): void {
    const config: CustomAppModalData = {
      title: 'Attachment Preview',
      data: {
        name: null,
        url: `data:application/pdf;base64,${base64}`,
        fileType: 'application/pdf',
      },
      showConfirm: false,
      showCancel: true,
      cancelText: 'Close',
      component: ExternalAttachmentPreview,
      hasFooter: false,
      widthClass: 'max-w-4xl',
    };

    this.modal.open(config);
  }
}
